import { useEffect, useRef } from "react";
import { Button } from "./Button";
import { Modal } from "./dialogs/Modal";

/** One term and the one sentence that says what it is. */
export interface BasisPair {
  term: string;
  def: string;
}

/**
 * One card's worth of method, titled with that card's own title.
 *
 * `id` is what a door opens at: a page with several cards has several doors and
 * ONE surface, so the sheet scrolls to the section belonging to whichever door
 * was pressed.
 */
export interface BasisSection {
  id: string;
  title: string;
  pairs: BasisPair[];
}

export interface BasisDisclosureProps {
  /** The section to open at, or `null` for closed. */
  openAtId: string | null;
  onClose: () => void;
  title: string;
  closeLabel: string;
  sections: readonly BasisSection[];
  testId?: string;
  /**
   * Raise the sheet above an already-open modal, for the case a door lives in
   * ANOTHER dialog's header rather than in a report card — the till's refund
   * sheet is the first. Forwarded to `Modal`, which tints a lighter backdrop so
   * two layers do not compound to near-black.
   */
  elevated?: boolean;
}

/** Within this many pixels, "taller than its box" is sub-pixel line rounding. */
const SCROLL_OVERFLOW_TOLERANCE_PX = 1;

/**
 * The element that actually scrolls the sheet: the nearest ancestor (from
 * `start` up) whose computed `overflow-y` lets it scroll AND whose content is
 * taller than its box by more than a pixel.
 *
 * The old walk took the first element with `scrollHeight > clientHeight`. In
 * Arabic the sheet's body — `overflow-y: visible`, so it scrolls nothing —
 * measures 1–2px taller than its client box (sub-pixel line boxes), was taken
 * for the scroller, and the real panel (`overflow-y: auto`) never moved: every
 * section door in Arabic opened the sheet at its TOP (Phase 2 QA, Q1). Both
 * conditions are needed: the style says the element CAN scroll, the overflow
 * says there is somewhere to go.
 *
 * `overflowYOf` is injectable so the rule can be tested without a layout
 * engine. Returns `null` when nothing scrolls (the whole sheet fits).
 */
export function basisScrollContainer(
  start: HTMLElement | null,
  overflowYOf: (element: HTMLElement) => string = (element) =>
    getComputedStyle(element).overflowY,
): HTMLElement | null {
  for (let element = start; element; element = element.parentElement) {
    const overflowY = overflowYOf(element);
    if (
      (overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay") &&
      element.scrollHeight > element.clientHeight + SCROLL_OVERFLOW_TOLERANCE_PX
    ) {
      return element;
    }
  }
  return null;
}

/**
 * Where a report page's method lives — one surface, many doors.
 *
 * **Why this is in `@a2z/ui` and not in a page.** The restaurant's dayparts
 * report built this inline: a local `basis` array, a local `Modal`, and a
 * local mapping of the same array into `buildCsv`'s notes. It worked, and it
 * was copy one. The money report needs the same thing for nine cards' worth of
 * clauses, which makes it copy two — and the platform's rule is that the
 * second caller pushes the primitive down rather than duplicating it.
 *
 * **What the shape is for.** A long report cannot have one door per page (a
 * reader on the seventh card does not find a control at the top) and must not
 * have one sheet per card (that is the same lecture in smaller type, which is
 * the complaint this whole pattern exists to answer). So: one content surface,
 * sectioned, with a door in each card's header that opens it at that card's
 * section. `basisNotes` emits the same sections in the same order into the
 * CSV, so the screen, the sheet and the exported file cannot drift.
 *
 * Carries no words of its own — every string is a prop, because `@a2z/ui` has
 * no i18n and a basis clause is the most locale-sensitive text in the product.
 *
 * A `Modal variant="sheet"` rather than an anchored popover: a bottom sheet on
 * a phone and a centred dialog on a desk need no RTL flip maths and no second
 * scroll container at 390px. Never a `title` attribute — those do not fire on
 * touch, which is the device an owner actually holds.
 */
export function BasisDisclosure({
  openAtId,
  onClose,
  title,
  closeLabel,
  sections,
  testId,
  elevated = false,
}: BasisDisclosureProps) {
  const bodyRef = useRef<HTMLDivElement | null>(null);

  /*
   * Scroll to the pressed door's section after the sheet has opened.
   *
   * `ModalSheet` animates in over two frames and moves focus on the second, so
   * a scroll issued during this effect's first run lands before the panel has
   * a scroll height and does nothing. Two frames, then scroll — and `block:
   * "start"` rather than `scrollIntoView()`'s default, which centres and can
   * leave the heading above the panel's top edge.
   */
  useEffect(() => {
    if (!openAtId) return;
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        const body = bodyRef.current;
        const target = body?.querySelector<HTMLElement>(
          `[data-basis-section="${openAtId}"]`,
        );
        if (!body || !target) return;
        /*
         * Scroll the SHEET, not the page.
         *
         * `scrollIntoView` walks up to the nearest scrollable ancestor, and
         * inside a modal that is frequently the document rather than the
         * panel — so the sheet stayed at its top while the page behind it
         * moved. Finding the scroll container and setting `scrollTop` from the
         * two elements' own offsets is deterministic and cannot pick the wrong
         * ancestor.
         */
        const scroller = basisScrollContainer(body);
        if (!scroller) return;
        scroller.scrollTop =
          target.getBoundingClientRect().top -
          scroller.getBoundingClientRect().top +
          scroller.scrollTop;
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [openAtId]);

  return (
    <Modal
      isOpen={openAtId !== null}
      onClose={onClose}
      elevated={elevated}
      /*
       * `md`, not `sm`. This sheet is the one dialog in the product that is
       * nothing BUT prose — six or seven headed paragraphs explaining how a
       * report is computed — and at 452px it set that prose in a column narrow
       * enough to break most sentences over three lines, so the reader scrolled
       * a long way through a short explanation. 576px is one step up the same
       * scale, still a dialog rather than a page.
       */
      size="md"
      title={title}
      closeLabel={closeLabel}
      hideFooter
    >
      <div ref={bodyRef} data-testid={testId} className="flex flex-col gap-5">
        {sections.map((section) => (
          <section key={section.id} data-basis-section={section.id}>
            {/* The section heading is the CARD's title, verbatim. A reader who
                pressed a door inside "VAT and tax bases" has to land on a
                heading they recognise as the card they came from, not on a
                paraphrase of it. */}
            <h3 className="mb-2 text-[12px] font-bold uppercase tracking-wide text-[color:var(--text-secondary)]">
              {section.title}
            </h3>
            <dl className="flex flex-col gap-3">
              {section.pairs.map((pair) => (
                <div key={pair.term} className="flex flex-col gap-1">
                  <dt className="text-[13px] font-bold">{pair.term}</dt>
                  <dd className="text-[13px] leading-[1.5] text-[color:var(--text-secondary)]">
                    {pair.def}
                  </dd>
                </div>
              ))}
            </dl>
          </section>
        ))}
      </div>
    </Modal>
  );
}

/**
 * The door: one per card, in the card's header.
 *
 * A real `<button>` with `aria-haspopup="dialog"`, never a `Chip`. The two are
 * deliberately not interchangeable on a report page: a chip on a figure is a
 * STATUS ("estimate", "not a filing figure") and is inert, while this is an
 * ACTION. A doorbell that looks like a label teaches a reader that tapping
 * labels does nothing; a label that is secretly a button teaches them that
 * some words are tappable and leaves them guessing which.
 */
export function BasisDoorButton({
  label,
  onClick,
  expanded,
  testId,
}: {
  label: string;
  onClick: () => void;
  expanded: boolean;
  testId?: string;
}) {
  /*
   * The SHARED `Button`, not a hand-rolled one.
   *
   * This was a raw `<button>` with its own border, radius, padding and weight.
   * It never carried `data-ui="button"` — the attribute every look stylesheet
   * selects on — so the looks styled every control around it and skipped this
   * one: a thin light border and muted text where its neighbours had a 2px ink
   * border, a hard shadow and the display face. Standing in a report toolbar
   * beside the CSV download, it read as a different product.
   *
   * `sm` keeps the 28px it has always been wherever it sits inside a card; in
   * a row marked `data-ui="toolbar"` the looks raise it to the control height,
   * so it lines up with the date picker and the export beside it. `shrink-0`
   * is preserved because these rows wrap.
   */
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={onClick}
      aria-haspopup="dialog"
      aria-expanded={expanded}
      data-testid={testId}
      className="shrink-0"
    >
      {label}
    </Button>
  );
}

/**
 * The same clauses, as lines for a CSV's trailing notes block.
 *
 * The file has no door, so what moved off the canvas has to travel in the
 * export — and it has to be the SAME strings in the SAME order, which is the
 * whole reason this takes the sections the sheet renders rather than a second
 * list assembled beside it. A column of figures whose basis is knowable only
 * from the screen it came off is what an accountant reads wrong.
 */
export function basisNotes(sections: readonly BasisSection[]): string[] {
  const lines: string[] = [];
  for (const section of sections) {
    lines.push(`${section.title}:`);
    for (const pair of section.pairs) {
      lines.push(`${pair.term} — ${pair.def}`);
    }
  }
  return lines;
}
